import React from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkStatsBlockItem } from 'fym-shared/src/smartlinks/stats';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { describeBlock } from './describeBlock';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import styles from './SMLBlocksPanel.module.scss';

interface SMLBlocksPanelProps {
  blocks: SmartLinkStatsBlockItem[];
  /** The page's blocks, used to name each row. */
  widgets?: SerializedWidget[] | null;
  /**
   * False for a page that is not published: the empty panel then asks for a publish. A live page,
   * and the account view (no value), say "No taps yet" (QA findings lit-12, brk-22).
   */
  isLive?: boolean;
}

/**
 * "What fans tapped" — clicks attributed to the block that earned them. This is
 * the breakdown the old analytics could not produce at all.
 *
 * Rows are named from the block itself where the page still has it — a link's
 * label or the service it points at — because the stats carry only a uuid and a
 * type, which would render a column of rows all reading "Link".
 */
const SMLBlocksPanelComponent: React.FC<SMLBlocksPanelProps> = ({ blocks, widgets, isLive = true }) => {
  const intl = useIntl();
  const topClicks = blocks[0]?.clicks ?? 0;

  return (
    <Panel className={styles.panel}>
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.stats.blocks.title', defaultMessage: 'What fans tapped' })}
        right={intl.formatMessage({ id: 'app.sml.stats.blocks.byBlock', defaultMessage: 'by block' })}
      />
      <PanelBody>
        {blocks.length === 0 ? (
          <div className={styles.empty}>
            {isLive
              ? intl.formatMessage({
                  id: 'app.sml.stats.blocks.emptyLive',
                  defaultMessage: 'No taps yet. Taps show here when fans tap a button.',
                })
              : intl.formatMessage({
                  id: 'app.sml.stats.blocks.empty',
                  defaultMessage: 'Publish the page to start collecting taps.',
                })}
          </div>
        ) : (
          blocks.map((block) => (
            <div key={block.key} className={styles.row}>
              <span className={styles.name} title={describeBlock(intl, block.key, block.blockType, widgets)}>
                {describeBlock(intl, block.key, block.blockType, widgets)}
              </span>
              <span className={styles.track}>
                {/* Scaled against the best block, not the total, so the leader
                  always fills the bar and the rest read as a comparison. */}
                <span
                  className={styles.bar}
                  style={{ width: topClicks === 0 ? '0%' : `${(block.clicks / topClicks) * 100}%` }}
                />
              </span>
              <span className={styles.value}>{intl.formatNumber(block.clicks, { notation: 'compact' })}</span>
            </div>
          ))
        )}
      </PanelBody>
    </Panel>
  );
};

export const SMLBlocksPanel = TypedMemo(SMLBlocksPanelComponent);
